import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  ArrowLeft, Home, Users, MapPin, Building2, Utensils, Phone, 
  Download, FileText, Hash, User, Calendar, Heart, Droplet, 
  CheckCircle, XCircle, Printer, Share2, Copy
} from 'lucide-react';

// Photo Enlargement Modal
function PhotoModal({ isOpen, onClose, photoUrl, name }) {
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const fullUrl = photoUrl?.startsWith('http') ? photoUrl : `${API_BASE}${photoUrl}`;
  
  if (!isOpen) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-2 bg-white">
        <div className="relative">
          <img 
            src={fullUrl} 
            alt={name || 'Member Photo'}
            className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
          />
          {name && (
            <p className="text-center text-sm font-medium text-slate-700 mt-2 pb-2">{name}</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Member Avatar Component with click to enlarge
function MemberAvatar({ photoUrl, name, size = 'sm', clickable = true }) {
  const [hasError, setHasError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  
  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-xs',
    md: 'w-12 h-12 text-sm',
    lg: 'w-16 h-16 text-lg',
    xl: 'w-24 h-24 text-xl'
  };
  
  const initials = name ? name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase() : '?';
  
  const handleClick = (e) => {
    if (clickable && photoUrl && !hasError) {
      e.stopPropagation();
      setShowModal(true);
    }
  };
  
  if (photoUrl && !hasError) {
    const fullUrl = photoUrl.startsWith('http') ? photoUrl : `${API_BASE}${photoUrl}`;
    return (
      <>
        <img 
          src={fullUrl} 
          alt={name || 'Member'}
          onError={() => setHasError(true)}
          onClick={handleClick}
          className={`${sizeClasses[size]} rounded-full object-cover flex-shrink-0 ${clickable ? 'cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all' : ''}`}
        />
        <PhotoModal 
          isOpen={showModal} 
          onClose={() => setShowModal(false)} 
          photoUrl={photoUrl} 
          name={name} 
        />
      </>
    );
  }
  
  return (
    <div className={`${sizeClasses[size]} rounded-full bg-slate-200 flex items-center justify-center text-slate-500 font-medium flex-shrink-0`}>
      {initials}
    </div>
  );
}

// Helper function to copy text to clipboard with fallback
const copyToClipboard = async (text, onSuccess, onError) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      onSuccess && onSuccess();
      return;
    }
  } catch (err) {
    // Clipboard API failed, try fallback
  }
  
  // Fallback: Create temporary textarea element
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    if (success) {
      onSuccess && onSuccess();
    } else {
      onError && onError();
    }
  } catch (err) {
    onError && onError();
  }
};

export default function FamilyViewPage() {
  const { hofId } = useParams();
  const navigate = useNavigate();
  const { api, user, isAdmin } = useAuth();
  const [familyData, setFamilyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExportDialog, setShowExportDialog] = useState(false);

  const fetchFamilyView = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/families/view/${hofId}`);
      setFamilyData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load family details');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [api, hofId, navigate]);

  useEffect(() => {
    fetchFamilyView();
  }, [fetchFamilyView]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!familyData) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Family not found</p>
        <Button onClick={() => navigate('/')} className="mt-4">Go to Dashboard</Button>
      </div>
    );
  }

  const { hof_details, members, total_members } = familyData;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="family-view-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-semibold text-slate-800">Family View</h1>
            <p className="text-sm text-slate-500">{total_members} members in this family</p>
          </div>
        </div>
        <Button onClick={() => setShowExportDialog(true)} variant="outline">
          <Download className="w-4 h-4 mr-2" />
          Export
        </Button>
        <Button onClick={() => window.print()} variant="outline">
          <Printer className="w-4 h-4 mr-2" />
          Print
        </Button>
      </div>

      {/* HOF Details Card */}
      <Card className="border-0 shadow-card bg-gradient-to-r from-navy/5 to-gold/5">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Home className="w-5 h-5 text-navy" />
            Head of Family Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-slate-500 font-medium">HOF Name</p>
              <p className="font-semibold text-slate-800">{hof_details.hof_name}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 font-medium">HOF ITS ID</p>
              {hof_details.hof_its ? (
                <button
                  onClick={() => copyToClipboard(
                    hof_details.hof_its,
                    () => toast.success(`Copied: ${hof_details.hof_its}`),
                    () => toast.error('Failed to copy')
                  )}
                  className="flex items-center gap-1 font-mono text-navy font-semibold hover:text-blue-600 hover:bg-blue-50 px-1 py-0.5 rounded cursor-pointer transition-colors"
                  title="Click to copy ITS"
                >
                  {hof_details.hof_its}
                  <Copy className="w-3 h-3 opacity-50" />
                </button>
              ) : (
                <p className="font-mono text-navy font-semibold">N/A</p>
              )}
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 font-medium">Mobile</p>
              <div className="flex items-center gap-1">
                <Phone className="w-3 h-3 text-slate-400" />
                <p className="font-medium">{hof_details.hof_mobile || 'N/A'}</p>
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 font-medium">HOF ID</p>
              <p className="font-mono text-xs">{hof_details.hof_id}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 font-medium">Mohalla</p>
              <div className="flex items-center gap-1">
                <Building2 className="w-3 h-3 text-slate-400" />
                <p className="font-medium">{hof_details.mohalla || 'Not Assigned'}</p>
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 font-medium">Sector</p>
              <div className="flex items-center gap-1">
                <MapPin className="w-3 h-3 text-slate-400" />
                <p className="font-medium">{hof_details.sector || 'Not Assigned'}</p>
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 font-medium">Address</p>
              <p className="text-sm">{hof_details.address || 'N/A'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 font-medium">Building Tag</p>
              <p className="font-medium">{hof_details.building_tag || 'N/A'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 font-medium">Flat Number</p>
              <p className="font-medium">{hof_details.flat_number || 'N/A'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 font-medium">Thaali Number</p>
              <div className="flex items-center gap-1">
                <Utensils className="w-3 h-3 text-slate-400" />
                <p className="font-mono font-semibold">
                  {hof_details.thaali_number || 'N/A'}
                </p>
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 font-medium">FMB Status</p>
              <Badge className={hof_details.fmb_status === 'Going' ? 'badge-success' : 'badge-warning'}>
                {hof_details.fmb_status}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Family Members Table */}
      <Card className="border-0 shadow-card overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="w-5 h-5 text-gold" />
            Family Members ({total_members})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="text-sm min-w-[1400px]">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold w-[50px]">Sr</TableHead>
                  <TableHead className="font-semibold w-[50px]"></TableHead>
                  <TableHead className="font-semibold min-w-[280px]">Full Name</TableHead>
                  <TableHead className="font-semibold w-[100px]">ITS ID</TableHead>
                  <TableHead className="font-semibold w-[100px]">Relation</TableHead>
                  <TableHead className="font-semibold w-[120px]">Mobile</TableHead>
                  <TableHead className="font-semibold w-[50px]">Age</TableHead>
                  <TableHead className="font-semibold w-[70px]">Gender</TableHead>
                  <TableHead className="font-semibold w-[70px]">Blood</TableHead>
                  <TableHead className="font-semibold w-[80px]">Misaq</TableHead>
                  <TableHead className="font-semibold w-[80px]">Marital</TableHead>
                  <TableHead className="font-semibold w-[80px]">Title</TableHead>
                  <TableHead className="font-semibold min-w-[120px]">Organisation</TableHead>
                  <TableHead className="font-semibold w-[100px]">Qualification</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow 
                    key={member.sr_no} 
                    className={member.is_hof ? 'bg-navy/5' : ''}
                    data-testid={`family-member-${member.sr_no}`}
                  >
                    <TableCell className="font-medium text-xs">{member.sr_no}</TableCell>
                    <TableCell className="p-2">
                      <MemberAvatar photoUrl={member.photo_url} name={member.full_name} size="sm" />
                    </TableCell>
                    <TableCell className="min-w-[280px]">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm" title={member.full_name}>{member.full_name}</span>
                        {member.is_hof && (
                          <Badge className="bg-navy text-white text-xs shrink-0">HOF</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {member.its_id ? (
                        <button
                          onClick={() => copyToClipboard(
                            member.its_id,
                            () => toast.success(`Copied: ${member.its_id}`),
                            () => toast.error('Failed to copy')
                          )}
                          className="flex items-center gap-1 font-mono text-xs text-navy font-semibold hover:text-blue-600 hover:bg-blue-50 px-1 py-0.5 rounded cursor-pointer transition-colors"
                          title="Click to copy ITS"
                        >
                          {member.its_id}
                          <Copy className="w-3 h-3 opacity-50" />
                        </button>
                      ) : (
                        <span className="font-mono text-navy font-semibold text-xs">-</span>
                      )}
                    </TableCell>
                    <TableCell>{member.relationship || '-'}</TableCell>
                    <TableCell>{member.mobile || '-'}</TableCell>
                    <TableCell>{member.age || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {member.gender || '-'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-red-600 font-medium">{member.blood_group || '-'}</span>
                    </TableCell>
                    <TableCell>
                      {member.misaq_status === 'Done' ? (
                        <Badge className="badge-success flex items-center gap-1 w-fit">
                          <CheckCircle className="w-3 h-3" />
                          Done
                        </Badge>
                      ) : (
                        <Badge className="badge-warning flex items-center gap-1 w-fit">
                          <XCircle className="w-3 h-3" />
                          Not Done
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{member.marital_status || '-'}</TableCell>
                    <TableCell>{member.title || '-'}</TableCell>
                    <TableCell>{member.organization || '-'}</TableCell>
                    <TableCell>{member.qualification || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Export Dialog */}
      {showExportDialog && (
        <ExportDialog
          hofId={hofId}
          hofName={hof_details.hof_name}
          api={api}
          isAdmin={isAdmin}
          user={user}
          onClose={() => setShowExportDialog(false)}
        />
      )}
    </div>
  );
}

function ExportDialog({ hofId, hofName, api, isAdmin, user, onClose }) {
  const [groupBy, setGroupBy] = useState('sector');
  const [format, setFormat] = useState('excel');
  const [loading, setLoading] = useState(false);

  // Leadership roles can only export PDF
  const canExportExcel = isAdmin || user?.role === 'super_admin' || user?.role === 'admin';

  const handleExport = async () => {
    if (!canExportExcel && format === 'excel') {
      toast.error('You can only export PDF');
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(
        `/export/universal?export_type=families&format=${format}&group_by=${groupBy}`,
        { responseType: 'blob' }
      );
      
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `family_${hofName.replace(/\s+/g, '_')}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export started');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export Family Data</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label className="form-label">How do you want to extract?</Label>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sector">Sector-wise</SelectItem>
                <SelectItem value="mohalla">Mohalla-wise</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="form-label">Format</Label>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pdf">PDF</SelectItem>
                {canExportExcel && <SelectItem value="excel">Excel</SelectItem>}
              </SelectContent>
            </Select>
            {!canExportExcel && (
              <p className="text-xs text-amber-600 mt-1">Leadership roles can only export PDF</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button onClick={handleExport} disabled={loading} className="bg-navy hover:bg-navy-light">
            {loading ? 'Exporting...' : 'Export'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
